/**
 * Consent Mode signals and the banner categories that map onto them.
 *
 * Categories are what a user answers. Signals are gtag's vocabulary. The
 * shared `rio_consent` cookie stores categories as tri-state values; signals
 * are derived only when something has to be handed to gtag.
 */

export const CONSENT_SIGNAL_KEYS = [
  'security_storage',
  'functionality_storage',
  'personalization_storage',
  'analytics_storage',
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
] as const;

export type ConsentSignalKey = (typeof CONSENT_SIGNAL_KEYS)[number];

/** gtag's two states. There is no third; `unset` collapses here and nowhere else. */
export type ConsentValue = 'granted' | 'denied';

export type ConsentSignals = Record<ConsentSignalKey, ConsentValue>;

/**
 * The subset of signals covered by an explicit answer. Membership is the
 * provenance of a signal's value, not the value itself — a signal in here
 * can be `denied`, and that denial is the visitor's own.
 */
export type AnsweredConsentSignals = ReadonlySet<ConsentSignalKey>;

/**
 * A category the user has not answered. Distinct from `denied` so a later
 * explicit grant is still possible, and so a merge cannot treat silence as a
 * refusal that overwrites a stored answer.
 */
export type ConsentChoiceValue = 'granted' | 'denied' | 'unset';

/**
 * Consent model version recorded on the cookie.
 *
 * - v1: the analytics-only model (`rio_cookie_consent`, and any `consentMode`
 *   cookie written before the category model existed). One yes/no covering
 *   analytics.
 * - v2: the four optional categories below, including the Consent Mode v2 ad
 *   signals `ad_user_data` / `ad_personalization`.
 *
 * A stored version below `CURRENT_CONSENT_VERSION` means the user has never
 * been asked about the signals this version adds, so they are re-prompted once.
 */
export const CURRENT_CONSENT_VERSION = 2;

/**
 * Banner/settings categories. These are what a user actually sees and toggles;
 * signals are an implementation detail of the destinations.
 */
export const CONSENT_CATEGORIES = [
  'essential',
  'functional',
  'analytics',
  'adMeasurement',
  'adPersonalization',
] as const;

export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/** Every category except `essential`, which is fixed on and not a choice. */
export type OptionalConsentCategory = Exclude<ConsentCategory, 'essential'>;

export const OPTIONAL_CONSENT_CATEGORIES = [
  'functional',
  'analytics',
  'adMeasurement',
  'adPersonalization',
] as const satisfies readonly OptionalConsentCategory[];

/** The user's answer for each optional category, including "never asked". */
export type ConsentChoices = Record<
  OptionalConsentCategory,
  ConsentChoiceValue
>;

/**
 * What produced a stored record. `implied` appears only in records an earlier
 * build wrote from the region policy; it is still parsed, so such a cookie can
 * be recognised and read as no decision, and it is never written.
 */
export type ConsentSource = 'banner' | 'settings' | 'implied';

/**
 * The sources that record an actual decision, which is all a writer may stamp.
 * Keeping `implied` out of this type is what stops a policy overlay being
 * persisted as an answer — the bug that put the `implied` records in browsers
 * in the first place.
 */
export type ConsentDecisionSource = Exclude<ConsentSource, 'implied'>;

export const CONSENT_CATEGORY_SIGNALS: Record<
  ConsentCategory,
  readonly ConsentSignalKey[]
> = {
  essential: ['security_storage'],
  functional: ['functionality_storage', 'personalization_storage'],
  analytics: ['analytics_storage'],
  adMeasurement: ['ad_storage', 'ad_user_data'],
  adPersonalization: ['ad_personalization'],
};

export const UNSET_ALL_CHOICES: ConsentChoices = {
  functional: 'unset',
  analytics: 'unset',
  adMeasurement: 'unset',
  adPersonalization: 'unset',
};

export const DENY_ALL_CHOICES: ConsentChoices = {
  functional: 'denied',
  analytics: 'denied',
  adMeasurement: 'denied',
  adPersonalization: 'denied',
};

export const GRANT_ALL_CHOICES: ConsentChoices = {
  functional: 'granted',
  analytics: 'granted',
  adMeasurement: 'granted',
  adPersonalization: 'granted',
};

export function isChoiceGranted(value: ConsentChoiceValue): boolean {
  return value === 'granted';
}

/** True when every optional category holds an explicit answer. */
export function areAllChoicesAnswered(choices: ConsentChoices): boolean {
  return OPTIONAL_CONSENT_CATEGORIES.every(
    (category) => choices[category] !== 'unset',
  );
}

export function isConsentChoiceValue(
  value: unknown,
): value is ConsentChoiceValue {
  return value === 'granted' || value === 'denied' || value === 'unset';
}

export function isConsentSource(value: unknown): value is ConsentSource {
  return value === 'banner' || value === 'settings' || value === 'implied';
}

/**
 * Maps category choices onto gtag signals. `unset` becomes `denied` here —
 * gtag has no third state, and this is the only place that collapse is
 * allowed. Merges and persistence keep `unset` as `unset`.
 */
export function signalsFromChoices(choices: ConsentChoices): ConsentSignals {
  const signals: ConsentSignals = {
    // Essential is not a choice: strictly necessary storage is always on, and
    // Google expects `security_storage` to be present in the default call.
    security_storage: 'granted',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };

  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    if (choices[category] !== 'granted') continue;
    for (const signal of CONSENT_CATEGORY_SIGNALS[category]) {
      signals[signal] = 'granted';
    }
  }

  return signals;
}

/**
 * The signals the visitor answered for themselves, granted or denied. The
 * ones {@link signalsFromChoices} derived from an `unset` category are
 * absent, which is the information that collapse destroys: there they read
 * `denied`, indistinguishable from a refusal.
 *
 * A destination that only has to hand gtag a state needs the signals alone;
 * one whose own behaviour differs between "refused" and "not asked yet" —
 * the GTM feed's pre-consent buffer is the case — needs this beside them,
 * because the collapse cannot be undone downstream.
 */
export function answeredSignalsFromChoices(
  choices: ConsentChoices,
): AnsweredConsentSignals {
  const answered = new Set<ConsentSignalKey>();
  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    if (choices[category] === 'unset') continue;
    for (const signal of CONSENT_CATEGORY_SIGNALS[category]) {
      answered.add(signal);
    }
  }
  return answered;
}

/**
 * The only ad signal a pre-v2 writer ever set, and the stand-in for an ad
 * category whose own signals a cookie never mentions.
 *
 * `ad_user_data` and `ad_personalization` did not exist when those writers
 * were built, so their absence marks an unmigrated writer rather than a
 * refusal. The one toggle that governed this signal was described to the
 * visitor as "Targeting cookies: These cookies help display relevant ads
 * based on your usage", which covers measuring ads and personalizing them
 * alike — so an explicit answer to it is an answer for both ad categories.
 */
const LEGACY_AD_SIGNAL: ConsentSignalKey = 'ad_storage';

/**
 * Categories that fall back to {@link LEGACY_AD_SIGNAL}. `functional` and
 * `analytics` have no legacy stand-in: every writer that set them set all the
 * signals they own, so they resolve from those signals alone.
 */
const AD_CATEGORIES = new Set<OptionalConsentCategory>([
  'adMeasurement',
  'adPersonalization',
]);

/**
 * Reconstructs category choices from a gtag-shaped cookie. A signal that is
 * simply absent is `unset`, not `denied`.
 *
 * `functional` and `analytics` are `granted` only when every signal they own
 * is present and granted, `denied` if any owned signal is present and denied,
 * otherwise `unset`.
 *
 * An ad category reads the signals it owns first, ignoring the ones the cookie
 * omits, and falls back to {@link LEGACY_AD_SIGNAL} when it owns nothing the
 * cookie mentions. Owned signals are consulted first so a value a writer set
 * explicitly always outranks one inferred from the legacy toggle: an inferred
 * grant must never override a recorded denial.
 */
export function choicesFromLegacySignals(
  record: Record<string, unknown>,
): ConsentChoices {
  const choices: ConsentChoices = { ...UNSET_ALL_CHOICES };
  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    choices[category] = choiceFromLegacySignals(record, category);
  }
  return choices;
}

function choiceFromLegacySignals(
  record: Record<string, unknown>,
  category: OptionalConsentCategory,
): ConsentChoiceValue {
  const owned = CONSENT_CATEGORY_SIGNALS[category];
  if (!AD_CATEGORIES.has(category)) {
    return choiceFromEverySignal(record, owned);
  }
  const explicit = choiceFromPresentSignals(record, owned);
  if (explicit !== 'unset') return explicit;
  return choiceFromPresentSignals(record, [LEGACY_AD_SIGNAL]);
}

/**
 * Resolves a category that needs its whole signal set. Deny wins; a grant
 * requires every signal to be present and granted, so a partial cookie leaves
 * the category unanswered.
 */
function choiceFromEverySignal(
  record: Record<string, unknown>,
  signals: readonly ConsentSignalKey[],
): ConsentChoiceValue {
  let sawGranted = false;
  let sawDenied = false;
  let sawMissing = false;
  for (const signal of signals) {
    const value = record[signal];
    if (value === 'granted') sawGranted = true;
    else if (value === 'denied') sawDenied = true;
    else sawMissing = true;
  }
  if (sawDenied) return 'denied';
  if (sawGranted && !sawMissing) return 'granted';
  return 'unset';
}

/**
 * Resolves a category from the signals the cookie actually carries. Absences
 * are skipped rather than counted against a grant; deny still wins among the
 * values that are present. `unset` means the cookie mentioned none of them.
 */
function choiceFromPresentSignals(
  record: Record<string, unknown>,
  signals: readonly ConsentSignalKey[],
): ConsentChoiceValue {
  let sawGranted = false;
  for (const signal of signals) {
    const value = record[signal];
    if (value === 'denied') return 'denied';
    if (value === 'granted') sawGranted = true;
  }
  return sawGranted ? 'granted' : 'unset';
}

export const DENY_ALL_SIGNALS: ConsentSignals =
  signalsFromChoices(DENY_ALL_CHOICES);

function isConsentValue(value: unknown): value is ConsentValue {
  return value === 'granted' || value === 'denied';
}

/**
 * Coerces an arbitrary parsed value into a full signal set for the dual-written
 * `consentMode` cookie. Unknown or missing keys read as `denied` because this
 * payload is handed to gtag, which has no `unset`. `security_storage` is
 * forced granted: it is not a user choice.
 */
export function coerceConsentSignals(value: unknown): ConsentSignals | null {
  if (typeof value !== 'object' || value === null) return null;
  const record: Record<string, unknown> = { ...value };

  const signals: ConsentSignals = { ...DENY_ALL_SIGNALS };
  for (const key of CONSENT_SIGNAL_KEYS) {
    const candidate = record[key];
    if (isConsentValue(candidate)) signals[key] = candidate;
  }
  signals.security_storage = 'granted';
  return signals;
}

/**
 * Overlays `incoming` onto `stored`. An `unset` incoming value is ignored, so
 * a silent category can never wipe a stored `granted` or `denied`.
 */
export function mergeChoices(
  stored: ConsentChoices,
  incoming: ConsentChoices,
): ConsentChoices {
  const merged: ConsentChoices = { ...stored };
  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    if (incoming[category] === 'unset') continue;
    merged[category] = incoming[category];
  }
  return merged;
}

/**
 * Combines answers held in records that cannot be ordered in time. A denial
 * anywhere wins, a grant survives only where nothing contradicts it, and a
 * category no source answered stays `unset`.
 *
 * `hasConflict` reports that two sources held opposite explicit answers for
 * some category, so the surviving denial is the safe reading of a
 * disagreement rather than the visitor's known latest word.
 */
export function reconcileChoicesDenyWins(sources: readonly ConsentChoices[]): {
  choices: ConsentChoices;
  hasConflict: boolean;
} {
  const choices: ConsentChoices = { ...UNSET_ALL_CHOICES };
  let hasConflict = false;

  for (const category of OPTIONAL_CONSENT_CATEGORIES) {
    const isDenied = sources.some((source) => source[category] === 'denied');
    const isGranted = sources.some((source) => source[category] === 'granted');
    if (isDenied && isGranted) hasConflict = true;
    if (isDenied) choices[category] = 'denied';
    else if (isGranted) choices[category] = 'granted';
  }

  return { choices, hasConflict };
}
